import axios from "axios";
import { useTenantAuth } from "../context/TeanantAuthProvider";
import { TENANT_DOMAIN } from "./domain";
export const useAxios = () => {
  const { logout, getRefreshToken, getAccessToken, persistToken } =
    useTenantAuth();

  const refreshAccessToken = async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return null;
    }
    try {
      const response = await axios.post(
        `${TENANT_DOMAIN}/tenant/auth/refresh-token`,
        {
          refresh_token: refreshToken,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (!response?.data?.access_token) {
        throw new Error("No access_token found.");
      }
      const token = {
        access_token: response?.data?.access_token,
        refresh_token: refreshToken,
      };
      persistToken(token);
      return response.data.access_token;
    } catch (err) {
      return null;
    }
  };

  const axiosInstance = axios.create({
    baseURL: TENANT_DOMAIN,
    headers: { "Content-Type": "application/json" },
  });

  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach((p) => {
      if (error) p.reject(error);
      else {
        p.config.headers["Authorization"] = `Bearer ${token}`;
        p.resolve(axiosInstance(p.config));
      }
    });
    failedQueue = [];
  };

  // Request Interceptor
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;

      // Let the browser set the correct Content-Type (with boundary) for FormData
      if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response Interceptor
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (!originalRequest) return Promise.reject(error);

      if (error.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject, config: originalRequest });
          });
        }

        isRefreshing = true;

        try {
          const refreshToken = await refreshAccessToken();
          if (!refreshToken) {
            processQueue(new Error("Refresh failed"), null);
            logout();
            return Promise.reject(error);
          }

          processQueue(null, refreshToken);

          originalRequest.headers["Authorization"] = "Bearer " + refreshToken;
          return axiosInstance(originalRequest);
        } catch (err) {
          processQueue(err, null);
          logout();
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    },
  );

  return { axiosInstance };
};
